import { Slot } from '@radix-ui/react-slot';

import { textStyles } from './TextStyles';
import { VariantProps } from './cn';
import { FCC } from './types';

type SharedProps = {
  className?: string;
  [key: `data-${string}`]: string;
};

// Heading

type HeadingProps = VariantProps<typeof textStyles.heading> & SharedProps;

type HeadingSize = NonNullable<HeadingProps['size']>;

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const HeadingTagsBySize: Record<HeadingSize, HeadingTag> = {
  '3xl': 'h1',
  '2xl': 'h2',
  xl: 'h3',
  lg: 'h4',
  md: 'h5',
  sm: 'h6',
};

const Heading: FCC<HeadingProps> = ({
  children,
  className,
  color,
  responsive,
  size = '3xl',
  weight,
  ...props
}) => {
  const headingTag = HeadingTagsBySize[size ?? '3xl'];
  const Component = headingTag;

  return (
    <Component
      className={textStyles.heading({
        className,
        color,
        responsive,
        size,
        weight,
      })}
      {...props}
    >
      {children}
    </Component>
  );
};

// Body

type BodyProps = VariantProps<typeof textStyles.body> & SharedProps;

const Body: FCC<BodyProps> = ({
  className,
  color,
  responsive,
  size,
  weight,
  ...props
}) => (
  <Slot
    className={textStyles.body({
      className,
      color,
      responsive,
      size,
      weight,
    })}
    {...props}
  />
);

const Paragraph: FCC<BodyProps> = ({ children, ...props }) => (
  <Body {...props}>
    <p>{children}</p>
  </Body>
);

const Text: FCC<BodyProps> = ({ children, ...props }) => (
  <Body {...props}>
    <span>{children}</span>
  </Body>
);

// Caption

type CaptionProps = VariantProps<typeof textStyles.caption> & SharedProps;

const Caption: FCC<CaptionProps> = ({
  className,
  color,
  size,
  weight,
  ...props
}) => (
  <span
    className={textStyles.caption({
      className,
      color,
      size,
      weight,
    })}
    {...props}
  />
);

// Exports

export { Heading, Paragraph, Text, Caption };
